import moment from 'moment'

import { boardNameFamily } from '@/shared/boardNameFamily'
import { trackerProductForFamily, trackerProductImage } from '@/shared/trackerProduct'
import i18n from './i18n'

export default {
  capitalize(val) {
    return val.charAt(0).toUpperCase() + val.slice(1)
  },

  lowercase(val) {
    return val.toLowerCase()
  },

  uppercase(val) {
    return val.toUpperCase()
  },

  length(val) {
    return val.length
  },

  formatDate(value) {
    if (value) {
      return moment(String(value)).format('LLLL')
    }
  },

  formatDay(value) {
    if (value) {
      return moment(String(value)).format('LL')
    }
  },

  formatShortDay(value) {
    if (value) {
      return moment(String(value)).format('ll')
    }
  },

  formatShortDayHour(value) {
    if (value) {
      return moment(String(value)).format('lll')
    }
  },

  formatHour(value) {
    if (value) {
      return moment(String(value)).format('LT')
    }
  },

  formatTime(value) {
    if (value) {
      return moment(String(value)).fromNow()
    }
  },

  formatDateDDMMYYYY(value) {
    if (value) {
      return moment(String(value)).format('DD/MM/YYYY')
    }
  },

  formatDateLegible(dateString) {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString(navigator.language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  },

  formatDateInterval(value) {
    if (value && value.length > 1) {
      if (!window.Intl) {
        return this.formatTime(value[1])
      }

      const date1 = new Date(value[0])
      const date2 = new Date(value[1])
      const year1 = date1.getFullYear()
      const year2 = date2.getFullYear()

      const locale = navigator.language

      if (year1 === year2) {
        const month1 = date1.getMonth()
        const month2 = date2.getMonth()
        if (month1 === month2) {
          const options1 = { day: 'numeric' }
          const options2 = { day: 'numeric', month: 'short' }
          const day1 = date1.toLocaleDateString(locale, options1)
          const day2 = date2.toLocaleDateString(locale, options2)

          return i18n.global.t('date_from_to_fmt', {
            date1: day1,
            date2: day2,
          })
        } else {
          const options = { day: 'numeric', month: 'short' }
          const day1 = date1.toLocaleDateString(locale, options)
          const day2 = date2.toLocaleDateString(locale, options)

          return i18n.global.t('date_from_to_fmt', {
            date1: day1,
            date2: day2,
          })
        }
      } else {
        const options = { day: 'numeric', month: 'short', year: 'numeric' }
        const day1 = date1.toLocaleDateString(locale, options)
        const day2 = date2.toLocaleDateString(locale, options)

        return i18n.global.t('date_from_to_fmt', { date1: day1, date2: day2 })
      }
    }
    return ''
  },

  validityInterval(value) {
    if (value && value.length > 1) {
      if (!window.Intl) {
        return this.formatTime(value[1])
      }

      const date2 = new Date(value[1])
      const year2 = date2.getFullYear()
      if (year2 > 2100) {
        return i18n.global.t('shared_link_permanent')
      }

      return this.formatDateInterval(value)
    }
    return ''
  },

  niceMode(value) {
    return this.formatMode(value)
  },

  formatMode(value) {
    const modes = {
      1: i18n.global.t('standard_monitoring_frequencyy'),
      3: i18n.global.t('high_monitoring_frequency'),
      2: i18n.global.t('intense_monitoring_frequency'),
      4: i18n.global.t('plane'),
      7: i18n.global.t('lost_mode'),
      8: i18n.global.t('standard_monitoring_frequencyy') + ' + ' + i18n.global.t('alert_vehicle'),
      10: i18n.global.t('high_monitoring_frequency') + ' + ' + i18n.global.t('alert_vehicle'),
      9: i18n.global.t('intense_monitoring_frequency') + ' + ' + i18n.global.t('alert_vehicle'),
      21: i18n.global.t('standard_monitoring_frequencyy') + ' + Animal ',
      23: i18n.global.t('high_monitoring_frequency') + ' + Animal ',
      22: i18n.global.t('intense_monitoring_frequency') + ' + Animal ',
      27: i18n.global.t('lost_mode'),
      50: i18n.global.t('sct2_configutable_mode'),
    }

    if (Object.prototype.hasOwnProperty.call(modes, value)) {
      return modes[value]
    }

    var output = `Mode ${value}`
    return output
  },

  format_device_type(device) {
    if (device.type === 'tracker_01') {
      if (device.tracker_config.board_name) {
        if (device.tracker_config.board_name.startsWith('LWT2')) {
          return 'Tracker LWT2'
        }
        if (device.tracker_config.board_name.startsWith('SCT2')) {
          return 'Tracker SCT2'
        }
      }
      return 'Tracker LWT'
    }
    if (device.type === 'tracker_02') {
      return 'Tracker SCT'
    }
    if (device.type === 'tracker_03') {
      return 'Tracker LWT3'
    }
    if (device.type === 'triby') {
      return 'Triby'
    }
    if (device.type === 'tracker_04') {
      return 'Smart Dog Collar'
    }
    return device.type
  },

  image_board_name(board_name, usage = null) {
    return trackerProductImage(trackerProductForFamily(boardNameFamily(board_name), usage))
  },

  formatUsage(value) {
    switch (value) {
      case 'person':
        return i18n.global.t('person')
      case 'vehicle':
        return i18n.global.t('vehicle')
      case 'dog':
        return i18n.global.t('dog')
      case 'cat':
        return i18n.global.t('cat')
      case 'other':
        return i18n.global.t('other')
      case 'bike':
        return i18n.global.t('bike')
      case undefined:
        return i18n.global.t('undefined')
      default:
        return `Usage ${value}`
    }
  },

  formatStationary(value) {
    if (value < 0) {
      return ''
    }
    if (value < 8) {
      return i18n.global.t('moving')
    }
    if (value > 100) {
      const _hours = Math.round(value / 60)
      return i18n.global.t('stationary_for') + ` ${_hours} ` + i18n.global.t('hours')
    }
    return i18n.global.t('stationary_for') + ` ${value} minutes`
  },

  formatBattery(device) {
    if (device.tracker_status) {
      if (device.tracker_status.battery < 0) {
        return '?'
      }

      if (device.type === 'tracker_02') {
        return device.tracker_status.battery + 'mV'
      }
      return device.tracker_status.battery + '%'
    }
    return '?'
  },

  formatAmount(unitAmount, currency) {
    const locale = navigator.language
    const options = { style: 'currency', currency: currency }
    const numberFormat = new Intl.NumberFormat(locale, options)
    return numberFormat.format(parseInt(unitAmount) / 100)
  },

  formatSubscriptionDuration(duration) {
    if (duration >= 12) {
      return duration / 12 + (duration / 12 >= 2 ? ' ' + i18n.global.t('years') : ' ' + i18n.global.t('year'))
    } else {
      return i18n.global.t('monthKey', duration)
    }
  },

  totalDuration(subscription) {
    let months = parseInt(subscription.meta_data.months)
    if (subscription.meta_data.extra) {
      months += parseInt(subscription.meta_data.extra)
    }
    return months
  },

  formatSubscriptionMonthlyPrice(price) {
    const locale = navigator.language
    const options = { style: 'currency', currency: price.currency }
    const numberFormat = new Intl.NumberFormat(locale, options)

    return numberFormat.format(price.unit_amount / 100 / price.meta_data.months)
  },

  formatSubscriptionYearlyPrice(price) {
    const locale = navigator.language
    const options = { style: 'currency', currency: price.currency }
    const numberFormat = new Intl.NumberFormat(locale, options)

    return numberFormat.format((price.unit_amount / 100 / price.meta_data.months) * 12)
  },

  formatPrice(price) {
    const locale = navigator.language
    const options = { style: 'currency', currency: price.currency }
    const numberFormat = new Intl.NumberFormat(locale, options)
    const meta_data = price.meta_data
    if (meta_data.months === 0) {
      return 'no change'
    } else {
      return meta_data.months + ' ' + 'months' + ': ' + numberFormat.format(price.unit_amount / 100)
    }
  },

  fullPublicUrl(uuid) {
    const base_url = window.location.origin
    const _url = base_url + '/pub/trackers?token=' + uuid
    return _url
  },
}
